/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { autoUpdate, flip, offset, shift, useFloating, FloatingPortal } from '@floating-ui/react'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { WarningCircle, XmarkCircle } from 'iconoir-react'
import moment from 'moment'
import { ReactElement, useEffect, useState } from 'react'
import Spinner from './Spinner'
import { LazyWorkspaceCell } from './LazyWorkspaceCell'
import type { LocationJson } from 'app/types/entities'
import { isLocationIntegrated } from 'app/utils'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { run } from 'lib/run'

let fetching: Record<string, Promise<LocationJson | undefined>> = {}
let cache: Record<string, LocationJson | undefined> = {}

export async function loadLocation(locationId: string) {
  if (cache[locationId] != null) return cache[locationId]
  if (fetching[locationId] != null) return fetching[locationId]
  else if (!locationId || locationId === `undefined`) return
  return (fetching[locationId] = run(async () => {
    const loc = await gqlFetchOne<LocationJson>(
      `location`,
      locationId,
      `id name workspaceId lastPmsSyncDate sikkaOfficeId carestackId kollaConnectorId`,
    )
    return (cache[locationId] = loc)
  }))
}

export function LazyLocationPanel({
  locationId,
  referenceElement,
  isOpen,
}: {
  locationId?: string
  referenceElement: HTMLElement | null
  isOpen: boolean
}): ReactElement {
  const [location, setLocation] = useState<LocationJson>()
  const [loading, setLoading] = useState<boolean>(true)

  const { floatingStyles, refs } = useFloating({
    placement: `right-start`,
    open: isOpen,
    middleware: [offset(8), flip({ fallbackPlacements: [`left-start`], padding: 5 }), shift({ padding: 5 })],
    whileElementsMounted: autoUpdate,
  })

  useEffect(() => {
    refs.setReference(referenceElement)
  }, [referenceElement, refs])

  // Load on first open, not on mount: every table row mounts a panel it never
  // shows, and on the activities page that was one location request per row.
  useEffect(() => {
    if (!isOpen || !locationId) return
    if (location) return
    bg(loadLocation(locationId).then((location) => setLocation(location)))
  }, [isOpen, locationId])

  useEffect(() => setLoading(!location), [location])

  if (!isOpen) return <></>

  return (
    <FloatingPortal>
      <div ref={refs.setFloating} style={floatingStyles} className="z-50">
        <div className="min-w-[300px] max-w-fit shrink-0 rounded-lg border bg-surface-raised py-4 shadow-elevation-5">
          {loading ? (
            <div className="flex h-10 w-full items-center justify-center align-middle">
              <Spinner size={5} />
            </div>
          ) : (
            locationId &&
            location && (
              <>
                <div className="px-4">
                  <h6>{location.name}</h6>
                  <div className="mb-3 mt-2 flex gap-x-2 align-middle">
                    <div className="flex items-center gap-x-2 align-middle">
                      {isLocationIntegrated(location) ? (
                        (() => {
                          if (
                            location?.lastPmsSyncDate &&
                            moment(location?.lastPmsSyncDate).isAfter(moment().subtract(2, `days`))
                          ) {
                            return (
                              <>
                                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                <span className="text-ui-xs text-green-600">Active PMS Integration</span>
                              </>
                            )
                          } else {
                            return (
                              <>
                                <WarningCircle className="m-0 h-5 w-5 rounded-full bg-surface bg-opacity-40 p-0 text-amber-900" />
                                <span className="text-ui-xs text-amber-900">Inactive PMS integration</span>
                              </>
                            )
                          }
                        })()
                      ) : (
                        <>
                          <XmarkCircle className="h-5 w-5 text-red-400" />
                          <span className="text-ui-xs text-red-400">No PMS integration</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col border-t border-default px-4 pt-3">
                  {location?.workspaceId && (
                    <div className="text-body-sm text-strong">
                      <span className="font-bold">Workspace: </span>
                      <LazyWorkspaceCell workspaceId={location.workspaceId} />
                    </div>
                  )}
                </div>
              </>
            )
          )}
        </div>
      </div>
    </FloatingPortal>
  )
}
